import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useFlags } from "launchdarkly-react-client-sdk";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

import Button from "components/common/Button";
import CustomChip from "components/common/Chip";
import Text from "components/common/Text";
import RadioButton from "components/common/RadioButtons";
import CustomTooltip from "components/common/Tooltip";
import PageLoader from "components/common/PageLoader";
import RiveAnimation from "components/common/RiveAnimation";
import { AlertType } from "components/common/Snackbar";
import { usePermissions } from "components/Hook";
import { isEmptyObject } from "components/Screens/CommonUtils";

import { showSnackbar } from "store/actions/Utility";
import { GetPlatformConfig } from "store/actions/PlatformConfig";
import { AddCustomerBankInfo, ResetAddCustomerBankInfo } from "store/actions/Payment/AddBankAccountInfo";
import { baseUrl } from "util/APIBaseUrl";
import { getUserAdditionalInfo } from "selectors/user";
import { getPlatformConfig } from "selectors/platformConfig";
import {
  createFinancialConnectionsSetupIntent,
  finalizeFinancialConnectionsSetup,
  getClientSecretApi,
} from "services/customer";
import AddAchPaymentSchema from "validations/Onboarding/AddAchPaymentSchema";

import EdgeLogo from "components/Design/EdgeLogo/EdgeLogo";
import AvatarMenu from "components/Screens/CustomerOnboarding/Layout/AvatarMenu";

import { getLoggedInUserDetailsFromLocalStorage } from "helpers";

import AccountBalanceIcon from "@mui/icons-material/AccountBalanceOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlineOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCardOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import AddPaymentMethodV2Old from "components/Screens/Login/AddPaymentMethodV2Old";

import "components/Screens/CustomerOnboarding/AddPaymentMethod/AddPaymentMethod.scss";

const VIEW = {
  OPTIONS: "options",
  ACH: "ach",
  CREDIT_CARD: "creditCard",
};

// Inner credit card form component (needs Stripe context)
function CreditCardForm({ onBack, loading, setLoading, navigateTo, dispatch }) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/main/dashboard`,
        },
        redirect: "if_required",
      });

      if (error) {
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: error.message || "Failed to save payment method",
          })
        );
        setLoading(false);
      } else if (setupIntent && setupIntent.status === "succeeded") {
        setLoading(false);
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.success,
            message: "Payment method added successfully.",
          })
        );
        setTimeout(() => navigateTo("/main/dashboard"), 2000);
      }
    } catch (err) {
      dispatch(
        showSnackbar({
          dispatch,
          type: AlertType.error,
          message: err.message || "Failed to save payment method",
        })
      );
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-1" style={{ width: "100%" }}>
      <div className="flex items-center gap-025">
        <ArrowBackIcon className="back-button-icon" onClick={onBack} />
        <span className="heading-h3-semibold color-text-heading-primary">Enter your card details</span>
      </div>

      <div className="banner-bg">
        <p className="para-body-m-medium color-text-body-primary">
          Card payments include a surcharge. Pay with{" "}
          <span
            className="cursor-pointer color-text-action"
            onClick={onBack}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") onBack();
            }}
          >
            ACH Direct Debit
          </span>{" "}
          to avoid this fee.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="payment-element-container">
          <PaymentElement
            options={{
              layout: { type: "auto" },
              fields: {
                billingDetails: {
                  name: "auto",
                  email: "auto",
                  phone: "auto",
                  address: { country: "auto" },
                },
              },
            }}
          />
        </div>

        <div className="mt-125">
          <Button
            onClick={handleSubmit}
            name="Add payment method"
            size="large"
            label="Add payment method"
            isFullWidth
            isDisabled={!stripe || !elements}
            isLoading={loading}
          />
        </div>
      </form>
    </div>
  );
}

// ACH Bank Details form
function BankDetailsForm({ onBack, loading, setLoading, customerId, dispatch, stripeFc }) {
  const methods = useForm({
    resolver: yupResolver(AddAchPaymentSchema),
    mode: "onChange",
  });

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = methods;

  const onSubmit = async (data = {}) => {
    try {
      setLoading(true);
      dispatch(
        AddCustomerBankInfo({
          request: `${baseUrl}/customer/${customerId}/account`,
          body: JSON.stringify({
            customerID: customerId,
            ...data,
          }),
        })
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          dispatch,
          type: AlertType.error,
          message: "Failed to add payment method. Please try again.",
        })
      );
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col" style={{ width: "100%" }}>
      <div className="flex items-center gap-025 mb-1">
        <ArrowBackIcon className="back-button-icon" onClick={onBack} />
        <span className="heading-h3-semibold color-text-heading-primary">Enter your bank account details</span>
      </div>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-125">
          <div className="onboarding-input-group">
            <p className="para-body-m-medium text-headings-secondary">Account holder type</p>
            <div className="flex gap-1 flex-nowrap">
              <Controller
                name="accountHolderType"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <RadioButton
                    label="Company"
                    value="company"
                    withContainer
                    checked={field.value === "company"}
                    onChange={() => field.onChange("company")}
                  />
                )}
              />
              <Controller
                name="accountHolderType"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <RadioButton
                    withContainer
                    label="Individual"
                    value="individual"
                    checked={field.value === "individual"}
                    onChange={() => field.onChange("individual")}
                  />
                )}
              />
            </div>
          </div>

          <div className="onboarding-input-group">
            <p className="para-body-m-medium text-headings-secondary">Account type</p>
            <div className="flex gap-1 flex-nowrap">
              <Controller
                name="accountType"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <RadioButton
                    withContainer
                    label="Checking"
                    value="Checking"
                    checked={field.value === "checking"}
                    onChange={() => field.onChange("checking")}
                  />
                )}
              />
              <Controller
                name="accountType"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <RadioButton
                    withContainer
                    label="Savings"
                    value="Savings"
                    checked={field.value === "savings"}
                    onChange={() => field.onChange("savings")}
                  />
                )}
              />
            </div>
          </div>

          <Text label="Account holder name" name="accountHolderName" isPayment />

          <div className="input-group-add-payment" style={{ marginTop: "0" }}>
            <Text
              label="Account number"
              labelIcon={
                <CustomTooltip content="Your account number is the unique identifier assigned to your bank account.">
                  <InfoOutlinedIcon className="onboarding-add-payment-label-icon" />
                </CustomTooltip>
              }
              name="accountNumber"
              isPayment
            />
            <Text
              label="ACH routing number"
              labelIcon={
                <CustomTooltip content="Your ACH routing number is a 9-digit code that identifies your bank and branch.">
                  <InfoOutlinedIcon className="onboarding-add-payment-label-icon" />
                </CustomTooltip>
              }
              isPayment
              name="routingNumber"
            />
          </div>

          {stripeFc && (
            <>
              <p className="mandate-text-inline" style={{ marginTop: "16px" }}>
                By clicking <strong>Add payment method</strong>, you authorize Edge to debit the bank account specified
                above for any amount owed for charges arising from your use of Edge&apos;s services, until this
                authorization is revoked.
              </p>
              <p className="para-body-s-regular color-text-body-primary" style={{ marginTop: "12px" }}>
                Your payment information is encrypted and securely shared with Stripe for processing. We never store
                your card or bank account information.
              </p>
            </>
          )}

          <Button
            onClick={handleSubmit(onSubmit)}
            name="Add payment method"
            size="large"
            label="Add payment method"
            isFullWidth
            isDisabled={!isValid}
            isLoading={loading}
            style={{ marginTop: "16px" }}
          />
        </form>
      </FormProvider>
    </div>
  );
}

function AddPaymentMethodV2() {
  const navigateTo = useNavigate();
  const dispatch = useDispatch();

  const { loggedInUser, accessToken } = usePermissions();
  const { stripeFc = false } = useFlags();
  const platformConfig = useSelector(getPlatformConfig);
  const { payload: platformPayload = {}, ok: isPlatformConfigLoaded = false } = platformConfig || {};
  const userAdditionalInfo = useSelector(getUserAdditionalInfo);
  const addBankAccountInfo = useSelector((state) => state.AddBankAccountInfo.addBankAccountInfo);
  const loadingAddBankAccountInfo = useSelector((state) => state.AddBankAccountInfo.loadingAddBankAccountInfo);

  const { skipPayment, creditCardSettings = {} } = userAdditionalInfo;
  const { allowCreditCard = false } = creditCardSettings ?? {};

  const [currentView, setCurrentView] = useState(VIEW.OPTIONS);
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

  const customerId = loggedInUser?.customerId || userAdditionalInfo?.customerId;
  const localUser = getLoggedInUserDetailsFromLocalStorage();
  const { name: fullName = "", email: fullEmail = "" } = localUser || {};
  const { pictureVersion = "" } = userAdditionalInfo;
  const avatarUrl = customerId ? `${baseUrl}/employee/${customerId}/logo?id=${pictureVersion}&variant=thumbnail` : "";

  const skipPaymentMethod = () => {
    if (accessToken && isEmptyObject(loggedInUser)) {
      navigateTo("/main/dashboard");
    } else {
      navigateTo("/");
    }
  };

  // Fetch client secret for credit card
  useEffect(() => {
    if (allowCreditCard && customerId && isPlatformConfigLoaded) {
      const fetchSecret = async () => {
        try {
          const data = await getClientSecretApi(customerId, dispatch);
          const secret = data?.client_secret || data?.clientSecret;
          setClientSecret(secret);
        } catch (err) {
          // Silent fail - credit card option will show loading state
        }
      };
      fetchSecret();
    }
  }, [allowCreditCard, customerId, isPlatformConfigLoaded, dispatch]);

  // Handle Plaid/bank account add result
  useEffect(() => {
    if (addBankAccountInfo?.ok) {
      setLoading(false);
      navigateTo("/main/dashboard");
    } else if (addBankAccountInfo?.error || typeof addBankAccountInfo === "string") {
      setLoading(false);
      dispatch(
        showSnackbar({
          dispatch,
          type: AlertType.error,
          message: addBankAccountInfo?.error?.[0]?.description || "Failed to add payment method. Please try again.",
        })
      );
    }
    dispatch(ResetAddCustomerBankInfo({}));
  }, [addBankAccountInfo, dispatch, navigateTo]);

  useEffect(() => {
    dispatch(GetPlatformConfig());
  }, [dispatch]);

  // Stripe Financial Connections handler
  const handleConnectViaStripeFC = useCallback(async () => {
    try {
      if (!platformPayload.STRIPE_PUBLISHABLE_KEY) {
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: "Stripe configuration is missing. Please try again later.",
          })
        );
        return;
      }

      setLoading(true);
      const stripe = await loadStripe(platformPayload.STRIPE_PUBLISHABLE_KEY);
      const fcResponse = await createFinancialConnectionsSetupIntent(customerId);
      const fcClientSecret = fcResponse?.clientSecret || fcResponse?.client_secret;

      if (!fcClientSecret) {
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: "Failed to initialize Stripe. Please try again.",
          })
        );
        setLoading(false);
        return;
      }

      setLoading(false);

      const currentUser = JSON.parse(localStorage.getItem("loggedInUser") || "{}");
      const userEmail = currentUser?.email || "";
      const userName = currentUser?.name || currentUser?.firstName || "Customer";

      const { setupIntent, error } = await stripe.collectBankAccountForSetup({
        clientSecret: fcClientSecret,
        params: {
          payment_method_type: "us_bank_account",
          payment_method_data: {
            billing_details: {
              name: userName,
              email: userEmail,
            },
          },
        },
      });

      if (error) {
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: error.message || "Failed to connect bank account",
          })
        );
        return;
      }

      if (setupIntent && setupIntent.payment_method) {
        setLoading(true);
        const result = await finalizeFinancialConnectionsSetup(customerId, setupIntent.id);
        if (result?.success) {
          setLoading(false);
          navigateTo("/main/dashboard");
        } else {
          dispatch(
            showSnackbar({
              dispatch,
              type: AlertType.error,
              message: "Failed to finalize bank connection. Please try again.",
            })
          );
          setLoading(false);
        }
      } else if (setupIntent && !setupIntent.payment_method) {
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: "Bank account connection was not completed. Please try again.",
          })
        );
      }
    } catch (error) {
      const errorMessage = error[0]?.description || error?.message || "Something went wrong, please try again";
      dispatch(
        showSnackbar({
          dispatch,
          type: AlertType.error,
          message: errorMessage,
        })
      );
      setLoading(false);
    }
  }, [platformPayload.STRIPE_PUBLISHABLE_KEY, customerId, dispatch, navigateTo]);

  const handleConnectWithStripeClick = () => {
    if (stripeFc) {
      handleConnectViaStripeFC();
    } else {
      // Fallback to ACH manual entry when Stripe FC is disabled
      setCurrentView(VIEW.ACH);
    }
  };

  const renderOptionsView = () => (
    <div className="flex flex-col gap-1" style={{ width: "100%" }}>
      <RiveAnimation
        style={{ marginLeft: "-20px", marginBottom: "-10px" }}
        src="/assets/animations/payment_method.riv"
        width="120px"
        height="132px"
      />
      <div className="heading-h1-semibold">Add payment method</div>
      <p className="para-body-m-regular color-text-body-primary">
        Set up your payment method to keep your operations running smoothly and avoid any interruptions. Your details
        are encrypted and safely stored for future use.
      </p>

      <div className="flex flex-col" style={{ marginTop: "16px" }}>
        {/* ACH Direct Debit Section */}
        <div className="flex flex-col gap-075">
          <p className="heading-h5-medium color-text-heading-primary">ACH direct debit</p>
          <div className="payment-options-divider" />
        </div>

        <div
          className="payment-option-row"
          onClick={handleConnectWithStripeClick}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === "Enter" || e.key === " ") handleConnectWithStripeClick();
          }}
        >
          <div className="payment-option-icon-circle">
            <AccountBalanceIcon className="payment-option-icon" />
          </div>
          <div className="payment-option-content">
            <div className="payment-option-title-row">
              <span className="payment-option-title">Connect with Stripe</span>
              <CustomChip label="Recommended" variant="filled" size="small" />
            </div>
            <p className="payment-option-description">Log in to your bank account to connect it as a payment method.</p>
          </div>
          <ChevronRightIcon className="payment-option-chevron" />
        </div>

        <div
          className="payment-option-row"
          onClick={() => setCurrentView(VIEW.ACH)}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === "Enter" || e.key === " ") setCurrentView(VIEW.ACH);
          }}
        >
          <div className="payment-option-icon-circle">
            <AccountBalanceIcon className="payment-option-icon" />
          </div>
          <div className="payment-option-content">
            <span className="payment-option-title">Enter bank details</span>
            <p className="payment-option-description">Manually enter your bank details.</p>
          </div>
          <ChevronRightIcon className="payment-option-chevron" />
        </div>

        {/* Other Options Section */}
        {allowCreditCard && (
          <>
            <div className="flex flex-col gap-075" style={{ marginTop: "32px" }}>
              <p className="heading-h5-medium color-text-heading-primary">Other options</p>
              <div className="payment-options-divider" />
            </div>

            <div
              className="payment-option-row"
              onClick={() => setCurrentView(VIEW.CREDIT_CARD)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") setCurrentView(VIEW.CREDIT_CARD);
              }}
            >
              <div className="payment-option-icon-circle">
                <CreditCardIcon className="payment-option-icon" />
              </div>
              <div className="payment-option-content">
                <div className="payment-option-title-row">
                  <span className="payment-option-title">Credit / Debit Card</span>
                  <span className="para-body-s-medium color-text-body-primary">Incl. surcharge</span>
                </div>
                <p className="payment-option-description">
                  Pay with any major credit or debit card. No bank account linking required.
                </p>
              </div>
              <ChevronRightIcon className="payment-option-chevron" />
            </div>
          </>
        )}
      </div>

      <p className="para-body-s-regular color-text-body-primary" style={{ marginTop: "16px" }}>
        Your payment information is encrypted and securely shared with Stripe for processing. We never store your card
        or bank account information.
      </p>

      {skipPayment && (
        <div style={{ marginTop: "8px" }}>
          <Button
            onClick={skipPaymentMethod}
            name="I'll add later"
            variant="text"
            size="large"
            label="I'll add later"
            isFullWidth={false}
          />
        </div>
      )}
    </div>
  );

  const renderCreditCardView = () => {
    let stripePromise = null;
    if (isPlatformConfigLoaded) {
      stripePromise = loadStripe(platformPayload.STRIPE_PUBLISHABLE_KEY);
    }

    if (!clientSecret) {
      return (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-025">
            <ArrowBackIcon className="back-button-icon" onClick={() => setCurrentView(VIEW.OPTIONS)} />
            <span className="heading-h3-semibold color-text-heading-primary">Loading payment form...</span>
          </div>
        </div>
      );
    }

    return (
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: "stripe",
            variables: {
              colorPrimary: "#8139d0",
              colorBackground: "#ffffff",
              colorText: "#7B7B7B",
              colorDanger: "#df1b41",
              fontFamily: "Inter, system-ui, sans-serif",
              spacingUnit: "4px",
              borderRadius: "5px",
              fontSizeBase: "16px",
            },
          },
        }}
      >
        <CreditCardForm
          onBack={() => setCurrentView(VIEW.OPTIONS)}
          loading={loading}
          setLoading={setLoading}
          navigateTo={navigateTo}
          dispatch={dispatch}
        />
      </Elements>
    );
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case VIEW.ACH:
        return (
          <BankDetailsForm
            onBack={() => setCurrentView(VIEW.OPTIONS)}
            loading={loading}
            setLoading={setLoading}
            customerId={customerId}
            dispatch={dispatch}
            stripeFc={stripeFc}
          />
        );
      case VIEW.CREDIT_CARD:
        return renderCreditCardView();
      default:
        return renderOptionsView();
    }
  };

  if (!stripeFc) {
    return <AddPaymentMethodV2Old />;
  }

  return (
    <>
      <Helmet>
        <title>Edge | Add Payment Method</title>
      </Helmet>
      {(loadingAddBankAccountInfo || loading) && <PageLoader />}

      <div style={{ minHeight: "100vh", backgroundColor: "#FFFFFF", width: "100%", overflowY: "auto" }}>
        {/* Topbar */}
        <div
          className="flex items-center justify-between"
          style={{
            height: "56px",
            padding: "0 32px 0 32px",
            borderBottom: "1px solid var(--color-border-primary)",
          }}
        >
          <EdgeLogo size="md" state="dark" />
          <AvatarMenu fullName={fullName} imageUrl={avatarUrl} email={fullEmail} />
        </div>

        {/* Title bar */}
        <div
          className="flex items-center justify-between"
          style={{
            padding: "8px 32px",
            borderBottom: "1px solid var(--color-border-primary)",
          }}
        >
          <p className="heading-h3-semibold">Complete account setup</p>
          <Button
            name="Need help?"
            variant="text"
            size="large"
            label="Need help?"
            isFullWidth={false}
            startIcon={<HelpOutlineIcon />}
          />
        </div>

        {/* Content */}
        <div style={{ padding: "20px" }}>
          <div style={{ width: "100%", maxWidth: "600px", margin: "0 auto" }}>{renderCurrentView()}</div>
        </div>
      </div>
    </>
  );
}

export default AddPaymentMethodV2;
